import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import SeoHead from "@/components/common/SeoHead";
import { ArrowRight, HeartHandshake, Wheat, Stethoscope, GraduationCap, Leaf, Coins, Lightbulb } from "lucide-react";
import { getImageUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const ICON_MAP = {
  "heart-handshake": HeartHandshake,
  "wheat": Wheat,
  "stethoscope": Stethoscope,
  "graduation-cap": GraduationCap,
  "leaf": Leaf,
  "coins": Coins,
  "lightbulb": Lightbulb,
};

export default function Programs() {
  const [items, setItems] = useState([]);
  const { settings } = useSettings();
  const pageData = settings?.programs_page || {};

  useEffect(() => {
    api.get("/programs").then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="page-programs">
      <SeoHead
        title={{ id: "Program", en: "Programs" }}
        description={{
          id: pageData.description || "Tujuh bidang program strategis Sauyunan Foundation — kemanusiaan, ketahanan pangan, kesehatan, pendidikan, lingkungan, ekonomi, dan inovasi.",
          en: "Seven strategic program areas — humanity, food systems, health, education, environment, economy, and innovation.",
        }}
      />
      <PageHeader
        eyebrow={pageData.eyebrow || "Program Kami"}
        title={pageData.title || "Tujuh bidang program strategis, satu ekosistem dampak"}
        description={pageData.description || "Setiap program dirancang saling terhubung untuk menghasilkan dampak sosial, ekonomi, dan lingkungan yang berkelanjutan lintas generasi."}
        image={pageData.image ? getImageUrl(pageData.image) : "https://images.unsplash.com/photo-1611087802810-046bb671e644?w=1200&q=80"}
      />

      <section className="section-y bg-white">
        <div className="container-sf">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((p, idx) => {
              const Icon = ICON_MAP[p.icon] || HeartHandshake;
              const accent = p.color === "accent";
              return (
                <Link
                  key={p.slug}
                  to={`/program/${p.slug}`}
                  className="card-sf overflow-hidden group flex flex-col"
                  data-testid={`programs-list-card-${p.slug}`}
                >
                  <div className="relative aspect-[4/2.8] overflow-hidden bg-slate-100">
                    <img src={getImageUrl(p.hero_image)} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className={`absolute top-4 left-4 h-11 w-11 rounded-xl flex items-center justify-center text-white shadow-lg ${accent ? "bg-[var(--brand-orange)]" : "bg-[var(--brand-blue)]"}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="absolute top-4 right-4 rounded-full bg-white/95 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-700">0{idx + 1}</div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{p.category}</div>
                    <h3 className="text-xl font-bold text-slate-900 leading-tight">{p.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3 flex-1">{p.short_description}</p>
                    <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-xs text-slate-500">{p.sub_programs?.length || 0} sub-program</span>
                      <span className="text-sm font-semibold text-[var(--brand-blue)] flex items-center gap-1 group-hover:text-[var(--brand-orange)] transition">
                        Selengkapnya <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
